import React, { useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import axios from 'axios'
import { toast } from 'react-toastify'
import { sleep } from 'src/mentor/helpers/utilityFunctions'
import { postMPDataWithParams, mentorApiURL } from 'src/mentor/services'
import { postSPDataWithParams, supportApiURL } from 'src/supportPortal/services/services'
import { getCountryCode, getMainLanguageCode } from 'src/mentor/services'

dayjs.extend(utc)

export default function usePrintableScreeningReportActions({
  setIsPrintingPastHistoryReports,
  isAlwaysPrinting,
  sendReportToTriggeringPractitionerApiSignature,
  screeningReport,
  screeningReportContainerRef,
  bookedEventInfo,
}) {
  const [isPrintingVar, setIsPrinting] = useState(false)
  const isPrinting = isAlwaysPrinting ? true : isPrintingVar

  const [saving, setSaving] = useState(false)
  const [sendingReport, setSendingReport] = useState(false)
  const [downloadingReport, setDownloadingReport] = useState(false)

  async function sendReportToTriggeringPractitioner() {
    if (!sendReportToTriggeringPractitionerApiSignature) {
      console.error('API signature not provided')
      return
    }

    try {
      setSendingReport(true)
      
      // Detect if this is a support portal API (contains /api/secure/cc/)
      const isSupportPortal = sendReportToTriggeringPractitionerApiSignature.includes('/api/secure/cc/')
      
      if (isSupportPortal) {
        // Support portal: reportType is in the path, only need eventId in query
        const params = {
          eventId: screeningReport?.bookedEventId,
        }
        const response = await postSPDataWithParams(
          sendReportToTriggeringPractitionerApiSignature,
          null,
          params,
        )
        return response
      } else {
        // Mentor portal: Use unified endpoint with reportType in query params
        // Query params: reportType (required), eventId OR reportId
        const params = {
          reportType: 'screening-report',
          eventId: screeningReport?.bookedEventId,
        }
        const response = await postMPDataWithParams(
          sendReportToTriggeringPractitionerApiSignature,
          null,
          params,
        )
        return response
      }
    } catch (error) {
      console.error('Failed to send report:', error)
      throw error
    } finally {
      setSendingReport(false)
    }
  }

  async function downloadScreeningReport() {
    if (!screeningReport?.bookedEventId) {
      console.error('Booked event ID not available')
      toast.error('Unable to download: Missing event ID')
      return
    }

    try {
      setDownloadingReport(true)
      
      const token = localStorage.getItem('token')
      const countryCode = getCountryCode()
      const mainLanguageCode = getMainLanguageCode()
      
      // Detect portal based on API signature if available, otherwise default to mentor
      const isSupportPortal = sendReportToTriggeringPractitionerApiSignature?.includes('/api/secure/cc/') || false
      
      const baseURL = isSupportPortal ? supportApiURL : mentorApiURL
      const endpoint = isSupportPortal 
        ? '/api/secure/cc/support/practitioner/emr-reports/download'
        : '/api/secure/mentor/emr-reports/download'
      
      const response = await axios.get(`${baseURL}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'ef-user-country-code': countryCode,
          'ef-app-language': mainLanguageCode,
        },
        params: {
          reportType: 'screening-report',
          eventId: screeningReport.bookedEventId,
          languageCode: mainLanguageCode,
          platform: 'web',
          countryCode: countryCode,
        },
        responseType: 'blob', // Important for PDF download
      })
      
      if (response?.status === 200 && response.data) {
        // Get filename from Content-Disposition header
        const contentDisposition = response.headers?.['content-disposition']
        let filename = 'screening-report.pdf'
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '')
          }
        }
        
        // Create blob and download
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        
        // Cleanup
        window.URL.revokeObjectURL(url)
        document.body.removeChild(link)
        
        toast.success('Report downloaded successfully')
      } else {
        toast.error('Failed to download report')
      }
    } catch (error) {
      console.error('Failed to download report:', error)
      toast.error('Failed to download report. Please try again.')
    } finally {
      setDownloadingReport(false)
    }
  }

  async function printScreening() {
    setIsPrintingPastHistoryReports(true)
    setIsPrinting(true)
    await sleep(500)

    // Get the report container element
    const reportElement = screeningReportContainerRef?.current
    
    if (!reportElement) {
      // Fallback to simple window.print() if no container ref
      window.print()
      setIsPrintingPastHistoryReports(false)
      setIsPrinting(false)
      return
    }

    let usedNewWindow = false

    try {
      // Clone the element to avoid modifying the original
      const clonedElement = reportElement.cloneNode(true)

      if (!clonedElement) {
        throw new Error('Failed to clone report element')
      }

      // Remove all UI elements from the cloned HTML
      const selectors = [
        '.hide-on-print-only',
        'button',
        '[class*="MuiButton"]',
        '[class*="MuiIconButton"]',
        '[class*="MuiDrawer"]',
        '[class*="MuiBackdrop"]',
        '[class*="MuiDialog"]',
        '[class*="MuiModal"]',
        '[class*="report-drawer-header"]',
        '[class*="report-drawer-actions"]',
        '[class*="MuiToolbar"]',
        '[class*="MuiAppBar"]',
        'input[type="button"]',
        'input[type="submit"]',
        'a[role="button"]',
        '[data-testid*="button"]',
        '[aria-label*="print"]',
        '[aria-label*="save"]'
      ]
      const elementsToRemove = clonedElement.querySelectorAll(selectors.join(', '))
      elementsToRemove.forEach(el => el.remove())

      // Remove fixed position elements (page indicators, overlays, etc.)
      const allElements = clonedElement.querySelectorAll('*')
      allElements.forEach(el => {
        const style = el.getAttribute('style') || ''
        const text = (el.textContent || '').trim().toLowerCase()

        const isFixed = style.includes('position: fixed') || style.includes('position:fixed')
        const hasBottom = style.includes('bottom:')
        const hasTransform = style.includes('transform:')
        const hasPageText = text.includes('page') || text.match(/page\s*\.?\s*\d*/)

        if (isFixed && (hasPageText || hasBottom || hasTransform)) {
          el.remove()
        }
      })

      // Get cleaned HTML
      let reportHTML = clonedElement.innerHTML || ''
      reportHTML = reportHTML.replace(/reactBusinessPortal/gi, 'AUMHUM')

      if (!reportHTML || reportHTML.trim().length === 0) {
        throw new Error('Report HTML is empty after processing')
      }

      // Get inline styles from the current document
      const inlineStyles = Array.from(document.querySelectorAll('style'))
        .map(style => style.innerHTML || '')
        .join('\n')

      // Create a new window for printing
      const printWindow = window.open('', '_blank', 'width=800,height=600')

      if (printWindow) {
        usedNewWindow = true

        // Generate filename format
        const patientNumber = screeningReport?.patientNumber || 'Unknown'
        let startTimeFormatted = ''

        if (bookedEventInfo?.startTime) {
          startTimeFormatted = dayjs.utc(bookedEventInfo.startTime).local().format('YYYYMMDD_HHmmss')
        } else if (screeningReport?.createdTime) {
          startTimeFormatted = dayjs.utc(screeningReport.createdTime).local().format('YYYYMMDD_HHmmss')
        } else {
          startTimeFormatted = dayjs().format('YYYYMMDD_HHmmss')
        }

        const filenameParts = `${patientNumber}_Screening_Report_${startTimeFormatted}`.split('_')
        const formattedTitle = filenameParts
          .map(part => (/^\d+$/.test(part) ? part : part.toUpperCase()))
          .join('-')

        const printedOn = dayjs().format('DD/MM/YYYY HH:mm')
        const printedBy = screeningReport?.mentorName || 'System'

        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${formattedTitle}</title>
              <meta charset="utf-8">
              <meta name="application-name" content="AUMHUM">
              <style>
                ${inlineStyles}
                * {
                  box-sizing: border-box;
                }
                html, body {
                  margin: 0 !important;
                  padding: 0 !important;
                  width: 100% !important;
                  height: auto !important;
                  background: white !important;
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                  overflow: visible !important;
                }
                ::-webkit-scrollbar {
                  display: none !important;
                }
                * {
                  scrollbar-width: none !important;
                  -ms-overflow-style: none !important;
                }
                div, section, article, main {
                  max-height: none !important;
                  height: auto !important;
                  overflow: visible !important;
                }
                
                /* Print footer - visible only when printing, fixed at bottom of each page */
                .print-footer {
                  display: none;
                }
                
                @page {
                  size: A4;
                  margin: 15mm 20mm 25mm 20mm;
                }
                
                @media print {
                  html, body {
                    margin: 0 !important;
                    padding: 0 !important;
                    overflow: visible !important;
                  }
                  
                  /* Show the print footer */
                  .print-footer {
                    display: block !important;
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 20mm;
                    background: white;
                    border-top: 1px solid #e0e0e0;
                    padding: 8px 20mm;
                    font-size: 10px;
                    color: #64748b;
                    z-index: 9999;
                  }
                  
                  .print-footer-content {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    height: 100%;
                  }
                  
                  .print-footer-left {
                    text-align: left;
                  }
                  
                  .print-footer-right {
                    text-align: right;
                  }
                  
                  /* Add padding at bottom of content to make room for fixed footer */
                  .print-content-wrapper {
                    padding-bottom: 25mm;
                  }
                  
                  .hide-on-print-only,
                  button,
                  [class*="MuiButton"],
                  [class*="MuiIconButton"],
                  [class*="MuiDrawer"],
                  [class*="MuiBackdrop"] {
                    display: none !important;
                  }
                  
                  /* Hide fixed elements EXCEPT print footer */
                  [style*="position: fixed"]:not(.print-footer),
                  [style*="position:fixed"]:not(.print-footer) {
                    display: none !important;
                  }
                  
                  h1, h2, h3, h4, h5, h6 {
                    page-break-after: avoid;
                    page-break-inside: avoid;
                  }
                  img {
                    page-break-inside: avoid;
                    max-width: 100% !important;
                  }
                }
                
                /* Screen only - hide print footer */
                @media screen {
                  .print-footer {
                    display: none !important;
                  }
                }
              </style>
            </head>
            <body>
              <div class="print-content-wrapper">
                ${reportHTML}
              </div>
              <div class="print-footer">
                <div class="print-footer-content">
                  <div class="print-footer-left">
                    Printed on: ${printedOn} | Printed by: ${printedBy}
                  </div>
                  <div class="print-footer-right">
                    AUMHUM Healthcare
                  </div>
                </div>
              </div>
            </body>
          </html>
        `)
        printWindow.document.close()

        // Wait for content to load then print
        printWindow.onload = () => {
          printWindow.document.title = formattedTitle

          // Clean up any remaining fixed elements
          const body = printWindow.document.body
          body.style.overflow = 'visible'
          body.style.height = 'auto'

          const allElems = body.querySelectorAll('*')
          allElems.forEach(el => {
            if (el.style.maxHeight) el.style.maxHeight = 'none'
            if (el.style.position === 'fixed') el.style.position = 'static'
          })

          setTimeout(() => {
            printWindow.focus()
            printWindow.print()
            setTimeout(() => {
              printWindow.close()
              setIsPrintingPastHistoryReports(false)
              setIsPrinting(false)
            }, 1000)
          }, 500)
        }

        // Fallback timeout
        setTimeout(() => {
          if (printWindow.document.readyState === 'complete') {
            printWindow.document.title = formattedTitle
            printWindow.focus()
            printWindow.print()
            setTimeout(() => {
              printWindow.close()
              setIsPrintingPastHistoryReports(false)
              setIsPrinting(false)
            }, 1000)
          }
        }, 2000)
      } else {
        // Fallback: use CSS-based print
        const printStyle = document.createElement('style')
        printStyle.id = 'screening-report-print-style'
        printStyle.textContent = `
          @media print {
            ::-webkit-scrollbar { display: none !important; }
            * { scrollbar-width: none !important; }
            body * { visibility: hidden !important; }
            .screening-report-print-content,
            .screening-report-print-content * {
              visibility: visible !important;
            }
            .screening-report-print-content {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
            }
            .hide-on-print-only,
            button,
            [class*="MuiButton"],
            [class*="MuiDrawer"],
            [class*="MuiBackdrop"] {
              display: none !important;
            }
            @page {
              size: A4;
              margin: 20mm;
            }
          }
        `
        document.head.appendChild(printStyle)
        reportElement.classList.add('screening-report-print-content')

        const handleAfterPrint = () => {
          reportElement.classList.remove('screening-report-print-content')
          const styleEl = document.getElementById('screening-report-print-style')
          if (styleEl) document.head.removeChild(styleEl)
          setIsPrintingPastHistoryReports(false)
          setIsPrinting(false)
          window.removeEventListener('afterprint', handleAfterPrint)
        }

        window.addEventListener('afterprint', handleAfterPrint)
        window.print()

        // Fallback cleanup
        setTimeout(() => {
          const styleEl = document.getElementById('screening-report-print-style')
          if (styleEl) handleAfterPrint()
        }, 2000)
      }
    } catch (error) {
      console.error('Error during print:', error)
      alert(`An error occurred while trying to print: ${error?.message || 'Unknown error'}. Please try again.`)
      setIsPrintingPastHistoryReports(false)
      setIsPrinting(false)
    }
  }

  return {
    isPrinting,
    printScreening,
    saving,
    sendReportToTriggeringPractitioner,
    sendingReport,
    downloadScreeningReport,
    downloadingReport,
  }
}
